import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Send, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { messagesAPI, socialAPI } from '../services/api';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const Chat = () => {
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const [messages, setMessages] = useState([]);
    const [otherUser, setOtherUser] = useState(null);
    const [newMessage, setNewMessage] = useState(location.state?.initialMessage || '');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        fetchChatData();
        // Poll for new messages
        const interval = setInterval(() => {
            fetchMessages(false);
        }, 10000);
        return () => clearInterval(interval);
    }, [userId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchChatData = async () => {
        setLoading(true);
        try {
            // Fetch user profile info
            const profileRes = await socialAPI.getProfile(userId);
            setOtherUser(profileRes.data);

            await fetchMessages(true);
        } catch (error) {
            console.error('Failed to load chat data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (markRead = false) => {
        try {
            const msgsRes = await messagesAPI.getMessages(userId);
            setMessages(msgsRes.data);

            if (markRead) {
                await messagesAPI.markAsRead(userId);
            }
        } catch (error) {
            console.error('Failed to load messages:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        try {
            const response = await messagesAPI.sendMessage(userId, { content: newMessage });
            setMessages([...messages, response.data]);
            setNewMessage('');
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    if (loading && !otherUser) {
        return (
            <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-snapp-green"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto h-[calc(100vh-64px)] flex flex-col bg-[#0a0a0b] lg:border-x border-gray-800">
            {/* Header */}
            <div className="bg-[#1a1a1b] p-3 sm:p-4 border-b border-gray-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/messages')} className="p-2 -ml-2 text-gray-400 hover:text-white">
                        <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    {otherUser && (
                        <div
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={() => navigate(`/profile/${otherUser.username}`)}
                        >
                            <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-700">
                                <AvatarImage src={otherUser.profile_image} />
                                <AvatarFallback className="bg-blue-600 text-white text-xs sm:text-sm">
                                    {otherUser.username?.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <h2 className="font-bold text-white text-sm sm:text-base leading-tight">
                                    {otherUser.name || otherUser.username}
                                </h2>
                                <span className="text-xs text-gray-400">@{otherUser.username}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 pt-6 space-y-4 bg-[#0a0a0b]">
                {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-6">
                        <div className="w-16 h-16 bg-[#1a1a1b] rounded-full flex items-center justify-center mb-4 border border-gray-800">
                            <Avatar className="h-12 w-12 grayscale opacity-50">
                                <AvatarImage src={otherUser?.profile_image} />
                                <AvatarFallback>{otherUser?.username?.substring(0, 2)}</AvatarFallback>
                            </Avatar>
                        </div>
                        <p className="text-gray-400">
                            Sohbeti başlatmak için bir mesaj gönder!
                        </p>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const isMe = msg.sender_id === user?.id;
                        const showTime = index === 0 ||
                            new Date(msg.created_at).getTime() - new Date(messages[index - 1].created_at).getTime() > 300000; // 5 mins

                        return (
                            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                {showTime && (
                                    <span className="text-[10px] text-gray-500 mb-2 mt-4 px-2 block w-full text-center">
                                        {format(new Date(msg.created_at), "d MMM HH:mm", { locale: tr })}
                                    </span>
                                )}
                                <div
                                    className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2 ${isMe
                                        ? 'bg-snapp-green text-[#0a0a0b] rounded-tr-sm'
                                        : 'bg-[#2a2a2b] text-white border border-gray-800 rounded-tl-sm'
                                        }`}
                                >
                                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 sm:p-4 bg-[#1a1a1b] border-t border-gray-800">
                <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
                    <Input
                        autoComplete="off"
                        placeholder="Mesaj yaz..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="flex-1 bg-[#2a2a2b] border-gray-700 text-white placeholder:text-gray-500 focus-visible:ring-snapp-green/50 min-h-[44px]"
                    />
                    <Button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="bg-snapp-green hover:bg-snapp-green/90 text-[#0a0a0b] h-[44px] w-[44px] sm:w-auto px-0 sm:px-6 shrink-0 rounded-full sm:rounded-md transition-all"
                    >
                        <span className="hidden sm:inline font-semibold">Gönder</span>
                        <Send className="w-5 h-5 sm:hidden" />
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default Chat;
